"use client";

import ChevronRightIcon from "@/components/core/icons/ChevronRightIcon";
import * as Collapsible from "@radix-ui/react-collapsible";
import clsx from "clsx";
import { ChevronDown, ChevronUp } from "lucide-react";
import Link from "next/link";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import {
  isExternalUrl,
  isNavigationLink,
  isNavigationLinkGroup,
  isSidebarNavigationItemActive,
  type SidebarNavigationLinkGroup,
  type SidebarNavigationLinkOrGroup,
} from "./navigation";

function NavLink({
  href,
  children,
  className,
  onMouseEnter,
  isSelected,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  onMouseEnter?: () => void;
  isSelected?: boolean;
}) {
  if (isExternalUrl(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onMouseEnter={onMouseEnter}
        aria-current={isSelected ? "page" : undefined}
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      href={href}
      className={className}
      onMouseEnter={onMouseEnter}
      aria-current={isSelected ? "page" : undefined}
    >
      {children}
    </Link>
  );
}

export default function SideNavigationItem({
  item,
  pathname,
  depth,
  trackId,
}: {
  item: SidebarNavigationLinkOrGroup;
  pathname: string;
  depth: number;
  /** Shared layoutId prefix for the active indicator within this group */
  trackId?: string;
}) {
  const router = useRouter();

  if (isNavigationLink(item)) {
    const isCurrent = pathname === item.href || pathname === item.href + "/";

    return (
      <div className="flex items-center relative">
        <div className={clsx("shrink-0", depth === 0 ? "w-5" : "w-0")} />
        {isCurrent && trackId && (
          <motion.div
            layoutId={trackId}
            className={clsx(
              "absolute w-0.5 h-4 bg-red-500",
              depth >= 2 ? "left-[-26px]" : "left-[-18px]",
            )}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
          />
        )}
        <NavLink
          href={item.href}
          onMouseEnter={
            !isExternalUrl(item.href)
              ? () => router.prefetch(item.href)
              : undefined
          }
          isSelected={isCurrent}
          className={clsx(
            "font-medium leading-[1.4] transition-colors duration-200 ease-in-out self-start hover:text-grey",
            depth === 0
              ? "text-sm py-1 px-2 -ml-2 rounded aria-[current=page]:bg-grey-850 bg-transparent"
              : "text-sm py-1.5",
          )}
        >
          <span className="font-medium flex flex-row gap-1">{item.title}</span>
        </NavLink>
      </div>
    );
  }

  if (isNavigationLinkGroup(item)) {
    return (
      <CollapsibleGroup
        item={item}
        pathname={pathname}
        depth={depth}
        trackId={trackId}
      />
    );
  }

  return null;
}

function CollapsibleGroup({
  item,
  pathname,
  depth,
  trackId,
}: {
  item: SidebarNavigationLinkGroup;
  pathname: string;
  depth: number;
  trackId?: string;
}) {
  const router = useRouter();
  const isActive = isSidebarNavigationItemActive(item, pathname);
  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isSidebarNavigationItemActive(item, pathname)) {
      setOpen(true);
    }
  }, [pathname, item]);

  // Each level gets its own trackId for the active indicator
  const groupTrackId = `track-${depth}`;

  return (
    <Collapsible.Root
      data-name="sidebar-group"
      open={open}
      onOpenChange={setOpen}
    >
      <>
        {/* Group header row */}
        <div
          data-name="sidebar-group-title"
          className={clsx(
            "flex items-center font-medium w-fit leading-4 self-start",
            clsx(
              depth === 0
                ? "text-sm py-1.5 px-2 -ml-2 rounded transition-colors duration-150 ease-in-out"
                : "text-sm py-1.5 px-2 -ml-2 rounded [[data-name=sidebar-group]+[data-name=sidebar-group]_&]:mt-1.5",
              isActive ? "bg-grey-850 hover:bg-grey-900" : "bg-transparent",
            ),
          )}
        >
          {depth < 1 && (
            <div className="w-3.5 shrink-0 flex items-center justify-center">
              <Collapsible.Trigger asChild>
                <div className="w-3.5 aspect-square cursor-pointer relative">
                  <div
                    className={clsx(
                      "absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 transition-all duration-500 ease-in-out",
                      open ? "rotate-90" : "rotate-0",
                      !open && !isActive && "text-grey-800",
                    )}
                  >
                    <ChevronRightIcon />
                  </div>
                </div>
              </Collapsible.Trigger>
            </div>
          )}

          <div className={depth < 1 ? "ml-2" : undefined}>
            {item.href ? (
              <NavLink
                href={item.href}
                onMouseEnter={
                  !isExternalUrl(item.href)
                    ? () => router.prefetch(item.href!)
                    : undefined
                }
                className={clsx(
                  "font-medium py-2 leading-none transition-colors duration-150 ease-in-out",
                  isActive ? "" : "hover:text-grey",
                )}
              >
                {item.title}
                {depth >= 1 && (
                  <Collapsible.Trigger
                    asChild
                    onClick={(e) => e.preventDefault()}
                  >
                    <span className="inline-flex items-center ml-1 align-middle">
                      {open ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </span>
                  </Collapsible.Trigger>
                )}
              </NavLink>
            ) : (
              <Collapsible.Trigger asChild>
                <span
                  className={clsx(
                    "cursor-pointer font-medium py-2 leading-none transition-colors duration-150 ease-in-out",
                    isActive ? "" : "hover:text-grey",
                  )}
                >
                  {item.title}
                  {depth >= 1 && (
                    <span className="inline-flex items-center ml-1 align-middle">
                      {open ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </span>
                  )}
                </span>
              </Collapsible.Trigger>
            )}
          </div>
        </div>

        {/* Collapsible children */}
        <Collapsible.Content
          data-name="sidebar-group-content"
          className={clsx(
            "py-2 data-[state=closed]:animate-collapse-up data-[state=open]:animate-collapse-down",
            depth < 1
              ? "overflow-hidden"
              : "overflow-y-clip overflow-x-visible",
          )}
        >
          <div className={clsx("relative", depth < 1 ? "pl-6" : "pl-2")}>
            {/* Vertical track line */}

            {depth < 1 && (
              <div className="absolute w-0.5 bg-grey-850 left-1.5 top-2 bottom-2" />
            )}

            {item.children.map((child, i) => (
              <SideNavigationItem
                key={
                  isNavigationLink(child)
                    ? child.href
                    : `group-${child.title}-${i}`
                }
                item={child}
                pathname={pathname}
                depth={depth + 1}
                trackId={groupTrackId}
              />
            ))}
          </div>
        </Collapsible.Content>
      </>
    </Collapsible.Root>
  );
}
